
import axios from 'axios';
import BasketContract from '../contracts/Basket';
import Item from './Item';
import Resource from './Resource';
import Order from './Order';
import Session from '../support/Session';

/**
 * Basket class
 * @class
 **/
class Basket extends Resource implements BasketContract
{
    /**
     * Shipping prices
     * @type {Array<*>}
     */
    static shipping = [];

    /**
     * Resource identifier
     * @type {String}
     **/
    static handle = 'basket_id';

    /**
     * Default attributes
     * @type {Object}
     **/
    static attributes: BasketContract = {
        basket_id: null,
        uuid: null,
    };

    /**
     * Resource relations
     * @type {Object}
     **/
    static relations = {
        items: {
            related: Item,
            default: [],
        },
    };

    /**
     * Resource attributes
     * @type {BasketContract}
     **/
    attributes: BasketContract;

    /**
     * Current attributes
     */
    basket_id: number;
    uuid: string;

    /**
     * Resource relationships
     */
    items: Item[];


    /**
     * Restore shopping basket
     * @return {Promise<Basket>}
     */
    static restore()
    {
        const uuid = Session.get( Basket.handle );
        if( uuid === null )
            return Promise.resolve( Session.basket = new Basket );

        return Basket.fetch( uuid ).catch( () => {
            Session.delete( Basket.handle );
            return new Basket;
        }).then( basket => Session.basket = basket );
    }


    /**
     * Fetch basket
     * @return {Promise<Basket>}
     */
    static fetch( uuid: string )
    {
        return axios.get( `/api/basket/${ uuid }` )
            .then( response => new Basket( response.data.basket, true ) );
    }


    /**
     * Get shipping cost
     * @returns {Number}
     */
    get shipping(): number
    {
        const weight = this.weight;
        if( weight === 0 )
            return 0;

        const shippingPrices = Basket.shipping.sort( ( a, b ) => b.weight - a.weight );
        for( const shipping of shippingPrices ) {
            if( weight >= shipping.weight )
                return shipping.price;
        }

        return 0;
    }


    /**
     * Get subtotal
     * @returns {Number}
     */
    get subtotal(): number
    {
        let subtotal = 0;
        for( const item of this.items )
            subtotal += item.total;

        return subtotal;
    }


    /**
     * Get total cost
     * @returns {Number}
     */
    get total(): number
    {
        return this.subtotal + this.shipping;
    }


    /**
     * Get basket total weight
     * @return {Number}
     */
    get weight()
    {
        let weight = 0;
        for( const item of this.items )
            weight += item.weight;

        return weight;
    }


    /**
     * Add item to basket
     * @param {Item} item
     * @return {Promise<>}
     */
    add( item: Item ): Promise<Basket>
    {
        const endpoint = this.exists ?
            `/api/basket/${ this.basket_id }` : '/api/basket';

        this.saving = true;
        return axios.put( endpoint, item.changes ).then( response => {
            Basket.shipping = response.data.shipping;
            this.sync( response.data.basket );
            Session.put( this );
            this.saving = false;

            return this as Basket;
        });
    }


    /**
     * Create order
     * @return {Promise<Order>}
     */
    order(): Promise<Order>
    {
        return axios.get( `/api/basket/${ this.basket_id }/order` )
            .then( response => new Order( response.data, true ) );
    }


    /**
     * Remove item from basket
     * @param {Item} item
     * @return {Promise<Item>}
     */
    remove( item: Item ): Promise<Basket>
    {
        const index = this.items.indexOf( item );
        if( index === -1 )
            return;

        this.saving = true;
        return axios.delete( `/api/basket/${ this.basket_id }`, {
            data: {
                item_id: item.item_id,
            }
        }).then( response => {
            Basket.shipping = response.data.shipping;
            this.sync( response.data.basket );
            this.saving = false;

            return this as Basket;
        });
    }
}

Resource.register( Basket );

export default Basket;
